import {
    Autocomplete,
    Button,
    CardActionArea,
    Checkbox,
    Drawer,
    Grid2,
    IconButton,
    Menu,
    MenuItem,
    Pagination,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import axios from "axios"
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react"
import {
    DialogContext,
    DialogType,
    RegistryControlContext,
    RegistryDataContext,
    RegistryFiltersContext,
    RegistryPaginationContext,
    RegistryPaginationSettingsContext,
    RegistryProvider,
    RegistrySortContext,
    Service
} from "avrora"
import {KeyboardArrowDown, KeyboardArrowUp, MoreVert} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router-dom";
import {AuthContext} from "../../context/auth.context";
import {validateEmail} from "../../utils/email";

export enum Role {
    Admin = "ADMIN",
    User = "USER",
    UserAdmin = "USER_ADMIN"
}

export const ROLES: Record<Role, string> = {
    [Role.Admin]: "Администратор",
    [Role.User]: "Пользователь",
    [Role.UserAdmin]: "Админ пользователей"
}

export enum UserStatus {
    Active = 'active',
    Blocked = 'blocked',
    Suspended = 'suspended',
    NotConfirmed = 'not_confirmed',
    Invited = 'invited',
    AwaitingPhoneConfirmation = 'awaiting_manual_check'
}

export const USER_STATUS: Record<UserStatus, string> = {
    [UserStatus.Active]: "Активен",
    [UserStatus.Blocked]: "Заблокирован",
    [UserStatus.Suspended]: "Отключен",
    [UserStatus.NotConfirmed]: "Не подтвержден",
    [UserStatus.Invited]: "Приглашен",
    [UserStatus.AwaitingPhoneConfirmation]: "Проверка оператором"
}

export const Users = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [countPerPage, setCountPerPage] = useState(20);
    const countPerPageRef = useRef(countPerPage);
    const currentPageRef = useRef(1);
    const shouldRefetchRef = useRef(false);

    countPerPageRef.current = countPerPage;

    const fetchListRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (shouldRefetchRef.current && fetchListRef.current) {
            shouldRefetchRef.current = false;
            fetchListRef.current();
        }
    }, [countPerPage]);

    const service = useMemo<Service<any>>(() => ({
        getList: params => {
            let data = {};

            const emailRegex = params.filterValues?.find((i: any) => i.name === "email")?.value;
            if (emailRegex) data = {...data, emailRegex};

            const fullNameRegex = params.filterValues?.find((i: any) => i.name === "fullName")?.value;
            if (fullNameRegex) data = {...data, fullNameRegex};

            const roles = params.filterValues?.find((i: any) => i.name === "role")?.value;
            if (roles?.length) data = {...data, roles};

            const status = params.filterValues?.find((i: any) => i.name === "status")?.value;
            if (status?.length) data = {...data, status};

            const pageSize = countPerPageRef.current;
            const currentPage = currentPageRef.current;

            return axios.post(`/eus/admin/user/filter?page=${currentPage}&pageSize=${pageSize}${params.order.length ? `&sortBy=${params.order[0].field}&sortOrder=${params.order[0].sort}` : ''}`, data).then(resp => ({
                count: resp.data.paging.totalElements,
                data: resp.data.users
            }));
        },
        getItem: i => axios.get(`/eus/user/${i.id}`).then(resp => resp.data),
        postItem: () => Promise.reject(),
        patchItem: () => Promise.reject(),
        putItem: () => Promise.reject(),
        removeItem: i => axios.delete(`/eus/user/${i.id}`)
    }), [countPerPage]);

    return (
        <RegistryProvider
            filterConfig={{
                email: {operator: "eq"},
                fullName: {operator: "eq"},
                role: {operator: "eq"},
                status: {operator: "eq"}
            }}
            id={params?.id}
            onOpenItem={id => navigate(`/page/users/${id}`)}
            service={service}
        >
            <Page countPerPageRef={countPerPageRef} setCountPerPage={setCountPerPage} shouldRefetchRef={shouldRefetchRef} fetchListRef={fetchListRef} currentPageRef={currentPageRef}/>
        </RegistryProvider>
    );
}

export const Page = ({countPerPageRef, setCountPerPage, shouldRefetchRef, fetchListRef, currentPageRef}: {countPerPageRef: React.MutableRefObject<number>, setCountPerPage: (value: number) => void, shouldRefetchRef: React.MutableRefObject<boolean>, fetchListRef: React.MutableRefObject<(() => void) | null>, currentPageRef: React.MutableRefObject<number>}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery("(max-width:499px)");

    const {data, item, remove} = useContext(RegistryDataContext);
    const {fetchList} = useContext(RegistryControlContext);
    fetchListRef.current = fetchList;
    const navigate = useNavigate();
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    const onAdmin = useCallback((id: string | number) => {
        axios.post(`/eus/user/give_admin`, [id]).then(fetchList);
    }, [fetchList]);

    const onRevokeAdmin = useCallback((id: string | number) => {
        axios.post(`/eus/user/revoke_admin`, [id]).then(fetchList);
    }, [fetchList]);

    const onUsersAdmin = useCallback((id: string | number) => {
        axios.post(`/eus/user/give_user_admin`, [id]).then(fetchList);
    }, [fetchList]);

    const onRevokeUsersAdmin = useCallback((id: string | number) => {
        axios.post(`/eus/user/revoke_user_admin`, [id]).then(fetchList);
    }, [fetchList]);

    const onLock = useCallback((id: string | number) => {
        axios.post(`/eus/admin/user/block/${id}`, [id]).then(fetchList);
    }, [fetchList]);

    const onUnlock = useCallback((id: string | number) => {
        axios.post(`/eus/admin/user/activate/${id}`, [id]).then(fetchList);
    }, [fetchList]);

    const onSuspend = useCallback((id: string | number) => {
        axios.post(`/eus/admin/user/suspend/${id}`, [id]).then(fetchList);
    }, [fetchList]);

    const onResend = useCallback((id: string | number) => {
        axios.post(`/eus/admin/user/confirm_email/${id}`, [id]).then(fetchList);
    }, [fetchList]);

    const onConfirmUser = useCallback((id: string | number) => {
        axios.post(`/eus/admin/user/confirm_user/${id}`, [id]).then(fetchList);
    }, [fetchList]);

    const onReset2FA = useCallback((id: string | number) => {
        axios.post(`/eus/admin/user/twofa-reset/${id}`).then(fetchList);
    }, [fetchList]);

    const onResetPass = useCallback((email: string) => {
        axios.post(`/eus/auth/request-reset`, {email}).then(fetchList);
    }, [fetchList]);

    const onRemove = useCallback((id: string | number) => {
        remove(id);
    }, [remove]);

    const dialogContext = useContext(DialogContext);

    const onMassiveBlock = useCallback((ids: string[]) => {
        dialogContext.push({
            text: `Вы точно хотите заблокировать ${ids.length} пользователей?`,
            title: "Подтвердите",
            type: DialogType.Confirm,
            onSubmit: () => {
                axios.post(`/eus/admin/user/massive-block`, ids).then(() => {
                    setSelectedUsers([]);
                    fetchList();
                });
            }
        });
    }, [fetchList, dialogContext]);

    const onMassiveActivate = useCallback((ids: string[]) => {
        dialogContext.push({
            text: `Вы точно хотите активировать ${ids.length} пользователей?`,
            title: "Подтвердите",
            type: DialogType.Confirm,
            onSubmit: () => {
                axios.post(`/eus/admin/user/massive-activate`, ids).then(() => {
                    setSelectedUsers([]);
                    fetchList();
                });
            }
        });
    }, [fetchList, dialogContext]);

    const onMassiveSuspend = useCallback((ids: string[]) => {
        dialogContext.push({
            text: `Вы точно хотите отключить ${ids.length} пользователей?`,
            title: "Подтвердите",
            type: DialogType.Confirm,
            onSubmit: () => {
                axios.post(`/eus/admin/user/massive-suspend`, ids).then(() => {
                    setSelectedUsers([]);
                    fetchList();
                });
            }
        });
    }, [fetchList, dialogContext]);

    const {user} = useContext(AuthContext);
    const {values, onChange: changeFilters} = useContext(RegistryFiltersContext);

    return (
        <Stack
            maxWidth={isMobile ? "100%" : "1024px"}
            minWidth={isMobile ? "0" : "1024px"}
            padding={isMobile ? "1rem" : "3rem"}
            spacing={2}
            sx={{ width: "100%", boxSizing: "border-box", overflowX: "hidden" }}
        >
            <Drawer
                anchor="right"
                PaperProps={{
                    style: {
                        width: isMobile ? "100%" : "512px",
                        maxWidth: "512px",
                        height: "100vh",
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0
                    }
                }}
                open={!!item}
                onClose={() => navigate('/page/users')}
            >
                <Card
                    item={item}
                    onRemove={() => onRemove?.(item?.id)}
                    onResetPass={() => onResetPass?.(item?.email)}
                    onAdmin={user?.scope?.split(' ')?.includes('admin') && !item?.roles.includes('ADMIN') ? (() => onAdmin?.(item?.id)) : undefined}
                    onRevokeAdmin={user?.scope?.split(' ')?.includes('admin') && item?.roles.includes('ADMIN') ? (() => onRevokeAdmin?.(item?.id)) : undefined}
                    onUsersAdmin={(user?.scope?.split(' ')?.includes('user_admin') || user?.scope?.split(' ')?.includes('admin')) && !item?.roles.includes('USER_ADMIN') ? (() => onUsersAdmin?.(item?.id)) : undefined}
                    onRevokeUsersAdmin={(user?.scope?.split(' ')?.includes('user_admin') || user?.scope?.split(' ')?.includes('admin')) && item?.roles.includes('USER_ADMIN') ? (() => onRevokeUsersAdmin?.(item?.id)) : undefined}
                    onLock={item?.actions?.includes("BLOCK") ? () => onLock?.(item?.id) : undefined}
                    onUnlock={item?.actions?.includes("ACTIVATE") ? () => onUnlock?.(item?.id) : undefined}
                    onSuspend={item?.actions?.includes("SUSPEND") ? () => onSuspend?.(item?.id) : undefined}
                    onResend={item?.status?.status === UserStatus.NotConfirmed ? () => onResend?.(item.id) : undefined}
                    onConfirmUser={item?.actions?.includes("CONFIRM_USER") ? () => onConfirmUser?.(item?.id) : undefined}
                    onReset2FA={item?.actions?.includes("RESET_2FA_SECRET") ? () => onReset2FA?.(item?.id) : undefined}
                />
            </Drawer>

            <Stack direction="column" spacing={2}>
                <Typography variant="h1" fontSize={isMobile ? "1.5rem" : "inherit"}>
                    Список пользователей
                </Typography>

                <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "flex-start" : "center"}
                    justifyContent="space-between"
                    spacing={2}
                >
                    <ToggleButtonGroup
                        color="primary"
                        size="small"
                        value={values.find(i => i.name === "status")?.value ?? []}
                        onChange={(_, value) => changeFilters([
                            ...values.filter(i => i.name !== 'status'),
                            {name: "status", value}
                        ])}
                        sx={{
                            flexWrap: "wrap",
                            gap: isMobile ? 0.5 : undefined
                        }}
                    >
                        {Object.values(UserStatus).map(i => (
                            <ToggleButton key={i} value={i}>
                                {USER_STATUS[i]}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>

                <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={2}
                    alignItems={isMobile ? "flex-start" : "center"}
                    justifyContent="space-between"
                >
                    <Stack direction="row" sx={{gap: isMobile ? 1 : 2}} flexWrap="wrap"
                           width={isMobile ? "100%" : "auto"}>
                        <TextField
                            size="small"
                            placeholder="Email"
                            fullWidth={isMobile}
                            sx={{maxWidth: isMobile ? "100%" : 200}}
                            value={values.find(i => i.name === "email")?.value ?? ''}
                            onChange={e => changeFilters([
                                ...values.filter(i => i.name !== 'email'),
                                {name: "email", value: e.target.value}
                            ])}
                        />

                        <TextField
                            size="small"
                            placeholder="Имя"
                            fullWidth={isMobile}
                            sx={{maxWidth: isMobile ? "100%" : 200}}
                            value={values.find(i => i.name === "fullName")?.value ?? ''}
                            onChange={e => changeFilters([
                                ...values.filter(i => i.name !== 'fullName'),
                                {name: "fullName", value: e.target.value}
                            ])}
                        />
                    </Stack>

                    <ToggleButtonGroup
                        color="primary"
                        size="small"
                        value={values.find(i => i.name === "role")?.value ?? []}
                        onChange={(_, value) => changeFilters([
                            ...values.filter(i => i.name !== 'role'),
                            {name: "role", value}
                        ])}
                        sx={{
                            flexWrap: "wrap",
                            gap: isMobile ? 0.5 : undefined
                        }}
                    >
                        {Object.values(Role).map(i => (
                            <ToggleButton key={i} value={i}>
                                {ROLES[i]}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    {selectedUsers.length > 0 && (
                        <Autocomplete
                            multiple
                            options={["block", "suspend", "activate"]}
                            value={[]}
                            onChange={(_, value) => {
                                if (value.includes("block")) {
                                    onMassiveBlock(selectedUsers);
                                }
                                if (value.includes("suspend")) {
                                    onMassiveSuspend(selectedUsers);
                                }
                                if (value.includes("activate")) {
                                    onMassiveActivate(selectedUsers);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Действия"
                                    sx={{ minWidth: 200 }}
                                />
                            )}
                            renderOption={(props, option) => {
                                const labels: Record<string, string> = {
                                    block: "Блокировать",
                                    suspend: "Отключить",
                                    activate: "Активировать"
                                };
                                return (
                                    <li {...props} key={option}>
                                        {labels[option]}
                                    </li>
                                );
                            }}
                            getOptionLabel={(option) => {
                                const labels: Record<string, string> = {
                                    block: "Блокировать",
                                    suspend: "Отключить",
                                    activate: "Активировать"
                                };
                                return labels[option] || option;
                            }}
                            componentsProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, 4],
                                            },
                                        },
                                    ],
                                },
                            }}
                        />
                    )}
                </Stack>

                <List
                    data={data}
                    selectedUsers={selectedUsers}
                    onSelectUser={(id) => {
                        if (selectedUsers.includes(id)) {
                            setSelectedUsers(selectedUsers.filter(i => i !== id));
                        } else {
                            setSelectedUsers([...selectedUsers, id]);
                        }
                    }}
                    onSelectAll={() => {
                        if (selectedUsers.length === data?.length) {
                            setSelectedUsers([]);
                        } else {
                            setSelectedUsers(data?.map((i: any) => i.id) || []);
                        }
                    }}
                    onAdmin={onAdmin}
                    onRevokeAdmin={onRevokeAdmin}
                    onUsersAdmin={onUsersAdmin}
                    onRevokeUsersAdmin={onRevokeUsersAdmin}
                    onLock={onLock}
                    onUnlock={onUnlock}
                    onSuspend={onSuspend}
                    onResend={onResend}
                    onConfirmUser={onConfirmUser}
                    onReset2FA={onReset2FA}
                    onRemove={onRemove}
                    onResetPass={onResetPass}
                />

                <Paging countPerPageRef={countPerPageRef} setCountPerPage={setCountPerPage} shouldRefetchRef={shouldRefetchRef} currentPageRef={currentPageRef}/>
            </Stack>
        </Stack>
    );
}

const Card = ({
                  item,
                  onAdmin,
                  onRevokeAdmin,
                  onUsersAdmin,
                  onRevokeUsersAdmin,
                  onLock,
                  onUnlock,
                  onSuspend,
                  onResend,
                  onConfirmUser,
                  onReset2FA,
                  onRemove,
                  onResetPass
              }: {
    item: any,
    onAdmin?: () => void,
    onRevokeAdmin?: () => void,
    onUsersAdmin?: () => void,
    onRevokeUsersAdmin?: () => void,
    onLock?: () => void,
    onUnlock?: () => void,
    onSuspend?: () => void,
    onResend?: () => void,
    onConfirmUser?: () => void,
    onReset2FA?: () => void,
    onRemove?: () => void,
    onResetPass?: () => void
}) => {
    const dialogContext = useContext(DialogContext);

    const handleAction = useCallback((callback: (() => void) | undefined, text: string) => {
        return (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (callback) {
                dialogContext.push({
                    text,
                    title: "Подтвердите",
                    type: DialogType.Confirm,
                    onSubmit: callback
                });
            }
        };
    }, [dialogContext]);

    return (
        <Stack
            width="100%"
            maxWidth="512px"
            padding="3rem"
            spacing={2}
        >
            <Typography variant="h1">Пользователь ID {item?.id}</Typography>

            <Stack spacing={2}>
                <Stack direction="row" alignItems="center">
                    <Stack width="90px">
                        <Typography>Email:</Typography>
                    </Stack>
                    <Stack flexGrow={1}>
                        <TextField
                            size="small"
                            fullWidth
                            disabled
                            slotProps={{input: {autoComplete: 'new-password'}}}
                            error={!!item?.email && !validateEmail(item.email)}
                            value={item?.email}
                        />
                    </Stack>
                </Stack>

                <Stack direction="row" alignItems="center">
                    <Stack width="90px">
                        <Typography>Имя:</Typography>
                    </Stack>
                    <Stack flexGrow={1}>
                        <TextField
                            size="small"
                            fullWidth
                            disabled
                            slotProps={{input: {autoComplete: 'new-password'}}}
                            value={item?.fullName}
                        />
                    </Stack>
                </Stack>

                <ul>
                    {onAdmin && <MenuItem onClick={handleAction(onAdmin, "Вы точно хотите выдать права админа?")}>Выдать
                        админа</MenuItem>}
                    {onRevokeAdmin &&
                        <MenuItem onClick={handleAction(onRevokeAdmin, "Вы точно хотите отобрать права админа?")}>Отобрать
                            админа</MenuItem>}
                    {onUsersAdmin && <MenuItem
                        onClick={handleAction(onUsersAdmin, "Вы точно хотите выдать права админа пользователей?")}>Выдать
                        админа пользователей</MenuItem>}
                    {onRevokeUsersAdmin && <MenuItem
                        onClick={handleAction(onRevokeUsersAdmin, "Вы точно хотите отобрать права админа пользователей?")}>Отобрать
                        админа пользователей</MenuItem>}
                    {onLock && <MenuItem
                        onClick={handleAction(onLock, "Вы точно хотите заблокировать пользователя?")}>Блокировать</MenuItem>}
                    {onUnlock && <MenuItem
                        onClick={handleAction(onUnlock, "Вы точно хотите разблокировать пользователя?")}>Разблокировать</MenuItem>}
                    {onSuspend && <MenuItem
                        onClick={handleAction(onSuspend, "Вы точно хотите выключить пользователя?")}>Выключить</MenuItem>}
                    {onResend && <MenuItem
                        onClick={handleAction(onResend, "Вы точно хотите отправить ссылку на подтверждение почты?")}>Отправить
                        письмо подтверждения почты</MenuItem>}
                    {onConfirmUser && <MenuItem
                        onClick={handleAction(onConfirmUser, "Вы точно хотите подтвердить регистрацию?")}>Подтвердить
                        регистрацию</MenuItem>}
                    {onReset2FA && <MenuItem
                        onClick={handleAction(onReset2FA, "Вы точно хотите отправить новую ссылку для привязки устройства?")}>Отправить новую ссылку для привязки устройства</MenuItem>}
                    <MenuItem onClick={handleAction(onResetPass, "Вы точно хотите сбросить пароль?")}>Сбросить
                        пароль</MenuItem>
                    <MenuItem
                        onClick={handleAction(onRemove, "Вы точно хотите удалить пользователя?")}>Удалить</MenuItem>
                </ul>
            </Stack>
        </Stack>
    );
}

const List = ({
                  data,
                  selectedUsers,
                  onSelectUser,
                  onSelectAll,
                  onAdmin,
                  onRevokeAdmin,
                  onUsersAdmin,
                  onRevokeUsersAdmin,
                  onLock,
                  onUnlock,
                  onSuspend,
                  onResend,
                  onConfirmUser,
                  onReset2FA,
                  onRemove,
                  onResetPass
              }: {
    data?: any[],
    selectedUsers?: string[],
    onSelectUser?: (id: string) => void,
    onSelectAll?: () => void,
    onAdmin?: (id: string) => void,
    onRevokeAdmin?: (id: string) => void,
    onUsersAdmin?: (id: string) => void,
    onRevokeUsersAdmin?: (id: string) => void,
    onLock?: (id: string) => void,
    onUnlock?: (id: string) => void,
    onSuspend?: (id: string) => void,
    onResend?: (id: string) => void,
    onConfirmUser?: (id: string) => void,
    onReset2FA?: (id: string) => void,
    onRemove?: (id: string) => void,
    onResetPass?: (email: string) => void,
}) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {user} = useContext(AuthContext);
    const {sortModel, onSortModelChange} = useContext(RegistrySortContext);

    const handleSort = (field: string) => {
        if (sortModel[0]?.field !== field) {
            onSortModelChange([{field, sort: "asc"}]);
        } else if (sortModel[0]?.sort === "asc") {
            onSortModelChange([{field, sort: "desc"}]);
        } else {
            onSortModelChange([]);
        }
    };

    return (
        <Stack>
            <Stack borderRadius={6} p={1} pl={2}>
                <Grid2 container spacing={1} alignItems="center">
                    <Grid2 size={{xs: 0.5, sm: 0.5}}>
                        <Checkbox
                            checked={data ? selectedUsers?.length === data.length && data.length > 0 : false}
                            indeterminate={data ? selectedUsers && selectedUsers.length > 0 && selectedUsers.length < data.length : false}
                            onChange={onSelectAll}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Grid2>
                    <Grid2 size={{xs: 11.5, sm: 4}}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">Email</Typography>
                            <IconButton
                                size="small"
                                color={sortModel[0]?.field === "email" ? "primary" : "default"}
                                onClick={() => handleSort("email")}
                            >
                                {sortModel[0]?.field === "email" && sortModel[0]?.sort === "desc"
                                    ? <KeyboardArrowDown/>
                                    : <KeyboardArrowUp
                                        sx={sortModel[0]?.field !== "email" ? {opacity: 0.5} : undefined}/>}
                            </IconButton>
                        </Stack>
                    </Grid2>

                    <Grid2 size={{xs: 12, sm: 3}}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">Имя</Typography>
                            <IconButton
                                size="small"
                                color={sortModel[0]?.field === "fullName" ? "primary" : "default"}
                                onClick={() => handleSort("fullName")}
                            >
                                {sortModel[0]?.field === "fullName" && sortModel[0]?.sort === "desc"
                                    ? <KeyboardArrowDown/>
                                    : <KeyboardArrowUp
                                        sx={sortModel[0]?.field !== "fullName" ? {opacity: 0.5} : undefined}/>}
                            </IconButton>
                        </Stack>
                    </Grid2>

                    <Grid2 size={{xs: 4, sm: 2}}>
                        <Typography variant="body2">Статус</Typography>
                    </Grid2>

                    <Grid2 size={{xs: 4, sm: 2}}>
                        <Typography variant="body2">Роли</Typography>
                    </Grid2>

                    <Grid2 size={{xs: 4, sm: 0.5}} display="flex" justifyContent="flex-end">
                        <Typography variant="body2">Действия</Typography>
                    </Grid2>
                </Grid2>
            </Stack>

            {data?.map((i: any) => (
                <Stack key={i.id} borderRadius={6} mt={0.5}>
                    <CardActionArea onClick={() => navigate(`/page/users/${i.id}`)}>
                        <Stack p={1} pl={2}>
                            <Grid2 container spacing={1} alignItems="center">
                                <Grid2 size={{xs: 0.5, sm: 0.5}}>
                                    <Checkbox
                                        checked={selectedUsers?.includes(i.id)}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            onSelectUser?.(i.id);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </Grid2>
                                <Grid2 size={{xs: 11.5, sm: 4}}>
                                    <Typography noWrap>{i.email}</Typography>
                                </Grid2>

                                <Grid2 size={{xs: 12, sm: 3}}>
                                    <Typography noWrap>{i.secondName + ' ' + i.firstName + ' ' + (i.surname ?? '')}</Typography>
                                </Grid2>

                                <Grid2 size={{xs: 4, sm: 2}}>
                                    <Typography>{USER_STATUS[i.status?.status as UserStatus]}</Typography>
                                </Grid2>

                                <Grid2 size={{xs: 4, sm: 2}}>
                                    <Typography>{i.roles.map((j: Role) => ROLES[j]).join(', ')}</Typography>
                                </Grid2>

                                <Grid2 size={{xs: 4, sm: 0.5}} display="flex" justifyContent="flex-end">
                                    <Actions
                                        onRemove={() => onRemove?.(i.id)}
                                        onAdmin={user?.scope?.split(' ')?.includes('admin') && !i.roles.includes('ADMIN') ? (() => onAdmin?.(i.id)) : undefined}
                                        onRevokeAdmin={user?.scope?.split(' ')?.includes('admin') && i.roles.includes('ADMIN') ? (() => onRevokeAdmin?.(i.id)) : undefined}
                                        onUsersAdmin={(user?.scope?.split(' ')?.includes('user_admin') || user?.scope?.split(' ')?.includes('admin')) && !i.roles.includes('USER_ADMIN') ? (() => onUsersAdmin?.(i.id)) : undefined}
                                        onRevokeUsersAdmin={(user?.scope?.split(' ')?.includes('user_admin') || user?.scope?.split(' ')?.includes('admin')) && i.roles.includes('USER_ADMIN') ? (() => onRevokeUsersAdmin?.(i.id)) : undefined}
                                        onLock={i.actions.includes("BLOCK") ? () => onLock?.(i.id) : undefined}
                                        onUnlock={i.actions.includes("ACTIVATE") ? () => onUnlock?.(i.id) : undefined}
                                        onSuspend={i.actions.includes("SUSPEND") ? () => onSuspend?.(i.id) : undefined}
                                        onResend={i.status?.status === UserStatus.NotConfirmed ? () => onResend?.(i.id) : undefined}
                                        onConfirmUser={i.actions.includes("CONFIRM_USER") ? () => onConfirmUser?.(i.id) : undefined}
                                        onReset2FA={i.actions.includes("RESET_2FA_SECRET") ? () => onReset2FA?.(i.id) : undefined}
                                        onResetPass={() => onResetPass?.(i.email)}
                                    />
                                </Grid2>
                            </Grid2>
                        </Stack>
                    </CardActionArea>
                </Stack>
            ))}
        </Stack>
    );
}

export const Actions = ({
                            onRemove,
                            onAdmin,
                            onRevokeAdmin,
                            onUsersAdmin,
                            onRevokeUsersAdmin,
                            onLock,
                            onUnlock,
                            onSuspend,
                            onResend,
                            onConfirmUser,
                            onReset2FA,
                            onResetPass
                        }: {
    onAdmin?: () => void,
    onRevokeAdmin?: () => void,
    onUsersAdmin?: () => void,
    onRevokeUsersAdmin?: () => void,
    onLock?: () => void,
    onUnlock?: () => void,
    onSuspend?: () => void,
    onResend?: () => void,
    onConfirmUser?: () => void,
    onReset2FA?: () => void,
    onRemove?: () => void,
    onResetPass?: () => void,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (e: any) => {
        e.stopPropagation();
        setAnchorEl(null);
    };

    const dialogContext = useContext(DialogContext);

    const handleAction = useCallback((callback: (() => void) | undefined, text: string) => {
        return (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            handleClose(event);
            if (callback) {
                dialogContext.push({
                    text,
                    title: "Подтвердите",
                    type: DialogType.Confirm,
                    onSubmit: callback
                });
            }
        };
    }, [dialogContext]);

    return (
        <div>
            <IconButton size="small" onClick={handleClick}><MoreVert/></IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
            >
                {onAdmin && <MenuItem onClick={handleAction(onAdmin, "Вы точно хотите выдать права админа?")}>Выдать
                    админа</MenuItem>}
                {onRevokeAdmin &&
                    <MenuItem onClick={handleAction(onRevokeAdmin, "Вы точно хотите отобрать права админа?")}>Отобрать
                        админа</MenuItem>}
                {onUsersAdmin && <MenuItem
                    onClick={handleAction(onUsersAdmin, "Вы точно хотите выдать права админа пользователей?")}>Выдать
                    админа пользователей</MenuItem>}
                {onRevokeUsersAdmin && <MenuItem
                    onClick={handleAction(onRevokeUsersAdmin, "Вы точно хотите отобрать права админа пользователей?")}>Отобрать
                    админа пользователей</MenuItem>}
                {onLock && <MenuItem
                    onClick={handleAction(onLock, "Вы точно хотите заблокировать пользователя?")}>Блокировать</MenuItem>}
                {onUnlock && <MenuItem
                    onClick={handleAction(onUnlock, "Вы точно хотите разблокировать пользователя?")}>Разблокировать</MenuItem>}
                {onSuspend && <MenuItem
                    onClick={handleAction(onSuspend, "Вы точно хотите выключить пользователя?")}>Выключить</MenuItem>}
                {onResend && <MenuItem
                    onClick={handleAction(onResend, "Вы точно хотите отправить ссылку на подтверждение почты?")}>Отправить
                    письмо подтверждения почты</MenuItem>}
                {onConfirmUser && <MenuItem
                    onClick={handleAction(onConfirmUser, "Вы точно хотите подтвердить регистрацию?")}>Подтвердить
                    регистрацию</MenuItem>}
                {onReset2FA && <MenuItem
                    onClick={handleAction(onReset2FA, "Вы точно хотите отправить новую ссылку для привязки устройства?")}>Отправить новую ссылку для привязки устройства</MenuItem>}
                {onResetPass && <MenuItem onClick={handleAction(onResetPass, "Вы точно хотите сбросить пароль?")}>Сбросить
                    пароль</MenuItem>}
                <MenuItem onClick={handleAction(onRemove, "Вы точно хотите удалить пользователя?")}>Удалить</MenuItem>
            </Menu>
        </div>
    );
};

const Paging = ({countPerPageRef, setCountPerPage, shouldRefetchRef, currentPageRef}: {countPerPageRef: React.MutableRefObject<number>, setCountPerPage: (value: number) => void, shouldRefetchRef: React.MutableRefObject<boolean>, currentPageRef: React.MutableRefObject<number>}) => {
    const {page, setPage} = useContext(RegistryPaginationContext);
    const {count: countPerPage} = useContext(RegistryPaginationSettingsContext);
    const {count} = useContext(RegistryDataContext);
    const {fetchList} = useContext(RegistryControlContext);
    const [pageSize, setPageSize] = useState(countPerPageRef.current || countPerPage);

    currentPageRef.current = page;

    const handlePageSizeChange = useCallback((newSize: number) => {
        countPerPageRef.current = newSize;
        setCountPerPage(newSize);
        setPageSize(newSize);
        setPage(1);
        shouldRefetchRef.current = true;
    }, [countPerPageRef, setCountPerPage, setPage, shouldRefetchRef, currentPageRef]);

    return (
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={2}>
            <Select
                size="small"
                value={pageSize}
                onChange={(e) => {
                    handlePageSizeChange(Number(e.target.value));
                }}
                sx={{ minWidth: 120 }}
            >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
            </Select>
            <Pagination
                page={page}
                count={Math.ceil((count ?? 0) / pageSize)}
                onChange={(_, page) => setPage(page)}
                size="small"
            />
        </Stack>
    );
};
